/*
 * Copyright 2026 North Carolina State University
 */

// frontend/src/components/SignIn.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/Auth.css';
import AuthBrandHeader from './AuthBrandHeader';
import { useBranding } from '../../contexts/BrandingContext';
import OtpVerification from './OtpVerification';

const SignIn = () => {
  const { branding } = useBranding();
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [awaitingOtp, setAwaitingOtp] = useState(false);
  const [otpNotice, setOtpNotice] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // Check for OIDC errors in URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const oidcError = params.get('error');
    
    if (oidcError === 'oidc_failed') {
      setError('Institutional sign-in failed. Please try again or contact support.');
      // Clean up URL
      navigate('/signin', { replace: true });
    }
  }, [location, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    const result = await login(credentials);
    if (result.success) {
      // Signed in with a temporary password from "Forgot password?" - go
      // straight to choosing a new one.
      if (result.mustChangePassword) {
        navigate('/profile', { state: { mustChangePassword: true } });
      } else {
        navigate('/');
      }
    } else if (result.requiresVerification) {
      setOtpNotice(result.codeSendFailed ? result.error : '');
      setAwaitingOtp(true);
    } else {
      setError(result.error || 'Failed to sign in');
    }
  };

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
  };

  const handleOIDCLogin = () => {
    // Redirect to OIDC login endpoint
    window.location.href = '/api/auth/oidc/login';
  };

  return (
    <div className="auth-page">
      {/* Logo and heading, arranged by an admin in Branding. The bundled logo
          stands in until one is uploaded. */}
      <AuthBrandHeader
        heading={branding.signin_heading || 'Plant Controlled Environment Laboratory Services Login'}
        subheading={branding.signin_subheading}
      />
      
      <div className="auth-container">
        {awaitingOtp ? (
          <OtpVerification
            email={credentials.email}
            initialError={otpNotice}
            onVerified={() => navigate('/')}
            onBack={() => setAwaitingOtp(false)}
          />
        ) : (
          <>
            {error && <div className="error-message">{error}</div>}

            {/* OIDC Sign In Button */}
            <div className="oidc-signin-section">
              <button
                type="button"
                className="oidc-button"
                onClick={handleOIDCLogin}
              >
                {branding.sso_button_label || 'Sign in with Institution ID'}
              </button>
              <div className="divider">
                <span>OR</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  name="email"
                  value={credentials.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Password:</label>
                <input
                  type="password"
                  name="password"
                  value={credentials.password}
                  onChange={handleChange}
                  required
                />
                <div className="forgot-password-link">
                  <Link to="/forgot-password" state={{ email: credentials.email }}>Forgot password?</Link>
                </div>
              </div>
              <button type="submit" className="auth-button">Sign In</button>
            </form>

            <div className="auth-link">
              <p>Don't have an account? <Link to="/signup">Sign Up</Link></p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SignIn;
